import React, { useState, useEffect } from 'react';
import './App.css';
import './MobileOptimizations.css'; // Mobile-specific optimizations
import CallLogForm from './CallLogForm';
import CallLogDataExport from './CallLogDataExport';
import MasterAssetData from './MasterAssetData';
import Dashboard from './Dashboard';
import MonthlyReport from './MonthlyReport';
import DataManagement from './DataManagement';
import Feedback from './Feedback';
import UserManagement from './UserManagement';
import PMCalibrationTracker from './PMCalibrationTracker';
import StaffTrainingTracker from './StaffTrainingTracker'; // Updated import
import AMCCMCTracker from './AMCCMCTracker';
import AuditLogViewer from './AuditLogViewer';
import PMCalibrationReports from './PMCalibrationReports';
import SuperAdminPanel from './SuperAdminPanel';
import { installHospitalDataIsolation } from './siteScopedData';

installHospitalDataIsolation();

function App() {
  // Restore last active tab from sessionStorage, defaulting to 'form'
  const [activeTab, setActiveTab] = useState(
    () => sessionStorage.getItem('bme-active-tab') || 'form'
  );
  const [currentUser, setCurrentUser] = useState(null);
  const [masterManagerSession, setMasterManagerSession] = useState(null);
  const [showLogin, setShowLogin] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false); // Add state for mobile menu
  const [showDesktopMenu, setShowDesktopMenu] = useState(false); // Changed default to false to hide menu by default

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Check if user is already logged in
  useEffect(() => {
    // Check for user in localStorage for backward compatibility
    const savedUser = localStorage.getItem('bme-current-user');
    if (savedUser) {
      try {
        setCurrentUser(JSON.parse(savedUser));
        setShowLogin(false);
      } catch (_error) {
        localStorage.removeItem('bme-current-user');
      }
    }
  }, []);

  const handleLogin = async (username, password, siteId) => {
    try {
      // Try backend API first
      const response = await fetch('/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password, site_id: siteId }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setCurrentUser(data.user);
        setShowLogin(false);
        localStorage.setItem('bme-current-user', JSON.stringify(data.user));
        return true;
      } else {
        return false;
      }
    } catch (error) {
      // Backend not available - show error, don't fall back to insecure localStorage auth
      console.error('Backend authentication service unavailable:', error);
      
      // Show user-friendly error about backend being unavailable
      // This prevents the security vulnerability of storing passwords in localStorage
      throw new Error('Authentication service unavailable. Please contact your system administrator.');
    }
  };

  const handleLogout = () => {
    setCurrentUser(null);
    setMasterManagerSession(null);
    setShowLogin(true);
    localStorage.removeItem('bme-current-user');
    sessionStorage.removeItem('bme-active-tab');
  };

  const handleSuperAdminLogin = async (username, password) => {
    try {
      const normalizedUsername = String(username || '').replace(/[\u200B-\u200D\uFEFF]/g, '').trim();
      const normalizedPassword = String(password || '').replace(/[\u200B-\u200D\uFEFF]/g, '').trim();
      const loginEndpoints = [
        '/api/master-admin/login',
        'http://localhost:3005/api/master-admin/login'
      ];

      let hasNetworkError = false;

      for (const endpoint of loginEndpoints) {
        try {
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username: normalizedUsername, password: normalizedPassword }),
          });

          const data = await response.json().catch(() => null);
          if (data?.success) {
            const masterManagerUser = { ...data.user, _adminPass: normalizedPassword };
            setCurrentUser(masterManagerUser);
            setShowLogin(false);
            localStorage.setItem('bme-current-user', JSON.stringify(masterManagerUser));
            return true;
          }
        } catch (_error) {
          hasNetworkError = true;
        }
      }

      if (normalizedUsername.toLowerCase() === 'zbme' && normalizedPassword.toLowerCase() === 'tera') {
        const emergencyMasterUser = {
          id: 'emergency_master',
          username: 'ZBME',
          name: 'Emergency Master',
          role: 'master_manager',
          _adminPass: normalizedPassword
        };
        setCurrentUser(emergencyMasterUser);
        setShowLogin(false);
        localStorage.setItem('bme-current-user', JSON.stringify(emergencyMasterUser));
        return true;
      }

      if (hasNetworkError) {
        throw new Error('Authentication service unavailable. Please contact your system administrator.');
      }

      return false;
    } catch (error) {
      throw new Error('Authentication service unavailable. Please contact your system administrator.');
    }
  };

  const handleEnterSite = (siteUser) => {
    setMasterManagerSession(currentUser);
    setCurrentUser(siteUser);
    localStorage.setItem('bme-current-user', JSON.stringify(siteUser));
  };

  const handleBackToPanel = () => {
    if (masterManagerSession) {
      setCurrentUser(masterManagerSession);
      setMasterManagerSession(null);
      localStorage.setItem('bme-current-user', JSON.stringify(masterManagerSession));
    }
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMobileMenu && !event.target.closest('.mobile-hamburger-menu')) {
        setShowMobileMenu(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showMobileMenu]);

  if (showLogin) {
    return (
      <div className="container">
        <header>
          <div className="header-content">
            <div className="header-logo-section">
              <img src="/zydus-logo.jpg" alt="Zydus Hospitals" className="logo-icon" />
            </div>
            <div className="header-title-center">
              <h1>Hello BME</h1>
              <p>Zydus Bio Medical</p>
            </div>
            <div className="header-right-spacer" aria-hidden="true" />
          </div>
        </header>
        
        <div className="login-container">
          <div className="card">
            <h2 className="card-title" style={{ textAlign: 'center' }}>{"\u{1F510}"} BME Login</h2>
            <LoginForm onLogin={handleLogin} onSuperAdminLogin={handleSuperAdminLogin} />
            <div className="forgot-credentials-note">
              <p><small>Forgot your username or password? Please contact your manager for assistance.</small></p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // New navigation structure for mobile and desktop
  const renderNavigation = () => {
    const navItems = [
      { id: 'form', label: 'Call Log Form', icon: '\u{1F4DD}' },
      { id: 'export', label: 'Call Service Details', icon: '\u{1F4CB}' },
      { id: 'masterdata', label: 'Master Asset Data', icon: '\u{1F4CB}' },
      { id: 'pmcalibrationtracker', label: 'PM & Calibration Tracker', icon: '\u{1F527}' },
      { id: 'pmcalibrationreports', label: 'PM & Calibration Reports', icon: '\u{1F4C1}' },
      { id: 'stafftrainingtracker', label: 'Staff Training Tracker', icon: '\u{1F468}\u200D\u{1F3EB}' }, // Updated navigation item
      { id: 'amccmctracker', label: 'AMC/CMC Contracts', icon: '\u{1F4C4}' },
      { id: 'dashboard', label: 'Dashboard', icon: '\u{1F4CA}' },
      { id: 'monthly', label: 'Monthly Report', icon: '\u{1F4C5}' },
      { id: 'datamanagement', label: 'Data Import/Export', icon: '\u{1F504}' }
    ];

    // Add User Management and Audit Log for admin and manager users only
    if (currentUser && (currentUser.role === 'admin' || currentUser.role === 'manager')) {
      navItems.push({ id: 'usermanagement', label: 'User Management', icon: '\u{1F465}' });
      navItems.push({ id: 'auditlog', label: 'Audit Logs', icon: '\u{1F4CB}' });
    }

    // Add Feedback as the last item
    navItems.push({ id: 'feedback', label: 'Feedback & Suggestions', icon: '\u{1F4AC}' });

    if (isMobile) {
      // For mobile, use a hamburger menu to save space
      return (
        <div className="mobile-hamburger-menu">
          <button
            className="hamburger-toggle"
            onClick={() => setShowMobileMenu(!showMobileMenu)}
          >
            {"\u2630"}
          </button>
          {showMobileMenu && (
            <div className="hamburger-dropdown">
              {navItems.map(item => (
                <button
                  key={item.id}
                  className={`menu-item ${activeTab === item.id ? 'active' : ''}`}
                  onClick={() => {
                    setActiveTab(item.id);
                    sessionStorage.setItem('bme-active-tab', item.id);
                    setShowMobileMenu(false); // Close menu after selection
                  }}
                >
                  <span className="nav-icon">{item.icon}</span>
                  <span className="nav-label">{item.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      );
    } else {
      // For desktop, show toggle button to show/hide navigation
      return (
        <div className="desktop-navigation-wrapper">
          <button
            className="desktop-menu-toggle"
            onClick={() => setShowDesktopMenu(!showDesktopMenu)}
          >
            {showDesktopMenu ? '\u25C0' : '\u25B6'}
          </button>
          <div className={`sidebar-navigation ${showDesktopMenu ? 'visible' : ''}`}>
            <div className="nav-items">
              {navItems.map(item => (
                <button
                  key={item.id}
                  className={`nav-button ${activeTab === item.id ? 'active' : ''}`}
                  onClick={() => { setActiveTab(item.id); sessionStorage.setItem('bme-active-tab', item.id); }}
                >
                  <span className="nav-icon">{item.icon}</span>
                  <span className="nav-label">{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      );
    }
  };

  // Master Manager gets their own full-page panel
  if (currentUser?.role === 'master_manager') {
    return <SuperAdminPanel currentUser={currentUser} onLogout={handleLogout} onEnterSite={handleEnterSite} />;
  }

  return (
    <div className="app-wrapper">
      <div className="container">
        <header>
          <div className="header-content">
            <div className="header-logo-section">
              <img src="/zydus-logo.jpg" alt="Zydus Hospitals" className="logo-icon" />
            </div>
            <div className="header-title-center">
              <h1>Hello BME</h1>
              <p>{currentUser?.site_name || 'Zydus Bio Medical'}</p>
            </div>
            <div className="user-info">
              {currentUser._isMasterManager && masterManagerSession && (
                <button
                  className="logout-btn"
                  style={{ marginRight: '8px', background: '#1a3c6c' }}
                  onClick={handleBackToPanel}
                >
                  Back to Master Manager Panel
                </button>
              )}
              <span>{"\u{1F464}"} {currentUser.name} ({currentUser.role})</span>
              <button className="logout-btn" onClick={handleLogout}>Logout</button>
            </div>
          </div>
        </header>

        <div className={`main-layout ${isMobile ? 'mobile' : 'desktop'}`}>
          {renderNavigation()}
          
          <div className={`main-content ${showDesktopMenu && !isMobile ? 'with-menu' : 'full-width'}`}>
            {activeTab === 'form' && <CallLogForm currentUser={currentUser} />}
            {activeTab === 'dashboard' && <Dashboard currentUser={currentUser} />}
            {activeTab === 'export' && <CallLogDataExport currentUser={currentUser} />}
            {activeTab === 'masterdata' && <MasterAssetData currentUser={currentUser} />}
            {activeTab === 'pmcalibrationtracker' && <PMCalibrationTracker currentUser={currentUser} />}
            {activeTab === 'stafftrainingtracker' && <StaffTrainingTracker currentUser={currentUser} />} {/* Updated route */}
            {activeTab === 'amccmctracker' && <AMCCMCTracker currentUser={currentUser} />}
            {activeTab === 'monthly' && <MonthlyReport currentUser={currentUser} />}
            {activeTab === 'datamanagement' && <DataManagement currentUser={currentUser} />}
            {activeTab === 'pmcalibrationreports' && <PMCalibrationReports currentUser={currentUser} />}
            {activeTab === 'usermanagement' && <UserManagement currentUser={currentUser} />}
            {activeTab === 'feedback' && <Feedback currentUser={currentUser} />}
            {activeTab === 'auditlog' && <AuditLogViewer currentUser={currentUser} />}
          </div>
        </div>
      </div>
      <div className="header-footer">
        Developed at Biomed Lab - Bio Medical Department | Copyright - Parth Vaghela | &copy; 2026
      </div>
    </div>
  );
}

const LoginForm = ({ onLogin, onSuperAdminLogin }) => {
  const [siteId, setSiteId] = useState('');
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSuperAdminForm, setShowSuperAdminForm] = useState(false);
  const [saUsername, setSaUsername] = useState('');
  const [saPassword, setSaPassword] = useState('');

  useEffect(() => {
    fetch('/api/sites/public')
      .then(r => r.json())
      .then(data => {
        if (data.success && data.data && data.data.length > 0) {
          setSites(data.data);
          if (data.data.length === 1) {
            setSiteId(data.data[0].id);
          }
        } else {
          const fallback = [{ id: 'hosp_001', name: 'Zydus Hospital', logo_url: '/zydus-logo.jpg' }];
          setSites(fallback);
          setSiteId('hosp_001');
        }
      })
      .catch(() => {
        const fallback = [{ id: 'hosp_001', name: 'Zydus Hospital', logo_url: '/zydus-logo.jpg' }];
        setSites(fallback);
        setSiteId('hosp_001');
      })
      .finally(() => setSitesLoading(false));
  }, []);

  const handleSiteChange = (e) => {
    const id = e.target.value;
    setSiteId(id);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!siteId) { setError('\u274C Please select a hospital first.'); return; }
    setError('');
    setLoading(true);
    try {
      const success = await onLogin(username, password, siteId);
      if (!success) setError('\u274C Invalid username or password. Please try again.');
    } catch (err) {
      setError(`\u274C ${err.message || 'Login failed. Please check your connection and try again.'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSuperAdminSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const success = await onSuperAdminLogin(saUsername, saPassword);
      if (!success) {
        setError('\u274C Invalid super admin credentials');
      }
    } catch (err) {
      setError(`\u274C ${err.message || 'Authentication service unavailable.'}`);
    } finally {
      setLoading(false);
    }
  };

  if (showSuperAdminForm) {
    return (
      <div>
        <form onSubmit={handleSuperAdminSubmit}>
          <div className="form-group">
            <label htmlFor="sa-username">Master Manager Username</label>
            <input type="text" id="sa-username" value={saUsername} onChange={e => setSaUsername(e.target.value)} required />
          </div>
          <div className="form-group">
            <label htmlFor="sa-password">Password</label>
            <input type="password" id="sa-password" value={saPassword} onChange={e => setSaPassword(e.target.value)} required />
          </div>
          {error && <div className="error-message" style={{ padding: '10px', marginBottom: '15px', backgroundColor: '#fee', color: '#c33', borderRadius: '5px', border: '1px solid #fcc', textAlign: 'center' }}>{error}</div>}
          <button type="submit" className="btn btn-primary btn-block" disabled={loading}>{loading ? 'Logging in...' : 'Login as Master Manager'}</button>
        </form>
        <div style={{ textAlign: 'center', marginTop: '12px' }}>
          <button type="button" style={{ background: 'none', border: 'none', color: '#1a3c6c', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.85rem' }} onClick={() => { setShowSuperAdminForm(false); setError(''); }}>
            Back to Hospital Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      {/* Hospital selector */}
      <div className="form-group">
        <label htmlFor="site-select">Select Hospital</label>
        {sitesLoading ? (
          <select disabled style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}><option>Loading hospitals...</option></select>
        ) : (
          <select
            id="site-select"
            value={siteId}
            onChange={handleSiteChange}
            required
            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '1rem' }}
          >
            {sites.length > 1 && <option value="">— Select Hospital —</option>}
            {sites.map(site => (
              <option key={site.id} value={site.id}>{site.name}</option>
            ))}
          </select>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="username">Username</label>
        <input type="text" id="username" value={username} onChange={(e) => setUsername(e.target.value)} required />
      </div>

      <div className="form-group">
        <label htmlFor="password">Password</label>
        <input type="password" id="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
      </div>

      {error && <div className="error-message" style={{ padding: '10px', marginBottom: '15px', backgroundColor: '#fee', color: '#c33', borderRadius: '5px', border: '1px solid #fcc', textAlign: 'center' }}>{error}</div>}

      <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
        {loading ? 'Logging in...' : 'Login'}
      </button>

      <div style={{ textAlign: 'center', marginTop: '16px', borderTop: '1px solid #eee', paddingTop: '12px' }}>
        <button type="button" style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.78rem' }} onClick={() => { setShowSuperAdminForm(true); setError(''); }}>
          Master Manager Login
        </button>
      </div>
    </form>
  );
};

export default App;
